import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatCourseDate } from "@/lib/dates";

export default function CursoDetalhe() {
  const { id } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [open, setOpen] = useState(false);
  const [participants, setParticipants] = useState(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/courses/${id}`).then(r => setCourse(r.data)).catch(() => {}); }, [id]);
  if (!course) return <p className="text-sm text-muted-foreground">Carregando…</p>;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads/express-enroll", {
        course_id: course.id,
        participants: Number(participants) || 1,
        notes,
      });
      toast.success("Inscrição enviada! Nossa equipe entrará em contato.");
      setOpen(false);
      navigate("/calendario");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar inscrição");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="course-detail">
      <Card className="rounded-2xl overflow-hidden">
        <div className="h-32 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))]" />
        <CardContent className="p-5 -mt-6">
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant="secondary" className="capitalize">{course.area}</Badge>
            <Badge variant="outline">{course.workload_hours}h</Badge>
            <Badge variant="outline" className="capitalize">{course.modality}</Badge>
          </div>
          <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">{course.title}</h1>
          <p className="text-sm text-muted-foreground mt-2">{course.description}</p>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-muted-foreground flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Datas</dt><dd className="font-medium">{formatCourseDate(course.start_date)} – {formatCourseDate(course.end_date)}</dd></div>
            <div><dt className="text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Local</dt><dd className="font-medium">{course.cidade}/{course.estado}</dd></div>
            <div><dt className="text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Carga horária</dt><dd className="font-medium">{course.workload_hours}h</dd></div>
            <div><dt className="text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Professor</dt><dd className="font-medium">{course.professor}</dd></div>
          </dl>
          <Button className="w-full mt-5 h-11" onClick={() => setOpen(true)} data-testid="express-enroll-button">
            Inscrição expressa
          </Button>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Inscrição expressa</DialogTitle>
            <DialogDescription>Seus dados serão pré-preenchidos. Confirme abaixo:</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input disabled value={user?.full_name || ""} /></div>
              <div><Label>CPF</Label><Input disabled value={user?.cpf || ""} /></div>
              <div><Label>E-mail</Label><Input disabled value={user?.email || ""} /></div>
              <div><Label>Telefone</Label><Input disabled value={user?.phone || ""} /></div>
              <div><Label>Órgão</Label><Input disabled value={user?.orgao || ""} /></div>
              <div><Label>Cidade/UF</Label><Input disabled value={`${user?.cidade || ""}/${user?.estado || ""}`} /></div>
            </div>
            <div><Label>Quantidade de participantes</Label>
              <Input type="number" min={1} value={participants} onChange={(e) => setParticipants(e.target.value)} data-testid="enroll-participants" /></div>
            <div><Label>Observações</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mensagem para nossa equipe" data-testid="enroll-notes" /></div>
            <Button className="w-full" onClick={submit} disabled={submitting} data-testid="enroll-submit">{submitting ? "Enviando…" : "Enviar inscrição"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
